/**
 * PURPOSE: Centralizar textos y formato de la auditoría administrativa.
 * RESPONSIBILITIES: Traducir módulos/campos, mostrar contexto del partido y formatear valores de detalle.
 * COLLABORATORS: AuditNotificationPanel.jsx y AuditNotificationDetails.jsx.
 * ROUTE: winscore/src/components/global/AuditNotificationPanel/auditDisplay.utils.js
 */
import { PORTAL_CAPABILITY_LABELS } from "../../../admin/AdminCompany/portalAccounts.utils.js";
import {
  UNIFORM_COLOR_NAMES,
  UNIFORM_TONE_LABELS,
  UNIFORM_TYPE_LABELS,
  formatLimaDateTime,
  utcToLimaLocal,
} from "../../../admin/AdminCompany/uniformOptions.utils.js";

export const MODULE_LABELS = {
  training: "Entrenamientos",
  match_schedule: "Programación",
  match_publication: "Publicación",
  participant_uniform: "Uniformes",
  // Administración por compañía (FTR-0012).
  institution: "Instituciones",
  venue: "Sedes",
  event: "Eventos",
  event_sport_configuration: "Deportes del evento",
  company_sport: "Deportes de la compañía",
  // Cuentas de delegado institucional (FTR-0013).
  portal_account: "Accesos de delegados",
  // Opciones de uniforme normalizadas y plazos de envío (FTR-0014).
  participant_uniform_option: "Opciones de uniforme",
  uniform_submission_policy: "Plazos de uniformes",
  // Elegibles Rugby (FTR-0015).
  match_lineup_submission: "Elegibles Rugby",
  rugby_lineup_rule: "Reglas de alineación Rugby",
  rugby_lineup_policy: "Plazos de elegibles Rugby",
  match_state: "Estado del partido",
  // Centro de Programación (FTR-0028). La programación de fecha/hora/sede/cancha que hace el Centro
  // sigue registrándose como `match_schedule`: es la misma operación, hecha desde otra pantalla.
  programming_draft: "Centro de Programación",
  programming_match: "Partido del Centro",
  // Cambio de participante 1/2 desde "Opciones avanzadas" (FTR-0039).
  match_participants: "Participantes del partido",
  // Podio / resultado por categoría individual (FTR-0057).
  individual_category_podium: "Podio individual",
  // Inscripciones de categorías individuales: agregar, retirar y mover de prueba (FTR-0059).
  individual_category_entry: "Inscripción individual",
};

// Filas de administración por compañía no tienen evento: se muestra la compañía (FTR-0012).
export const getAuditScopeLabel = (item) =>
  item?.event?.name || item?.context?.companyName || (item?.companyId ? `Compañía ${item.companyId}` : "");

export const ACTION_LABELS = {
  create: "Creación",
  update: "Edición",
  delete: "Eliminación",
  publish: "Publicación",
  unpublish: "Borrador",
  bulk_update: "Cambio masivo",
  reset_password: "Restablecimiento de contraseña",
  deactivate: "Desactivación",
  reactivate: "Reactivación",
  revoke_sessions: "Revocación de sesiones",
  reorder: "Reordenamiento",
  confirm: "Confirmación",
  grant_changes: "Cambios extra",
  start: "Inicio del partido",
  // Centro de Programación (FTR-0028).
  materialize: "Materialización",
  reorganize: "Reorganización",
};

export const FIELD_LABELS = {
  date: "Fecha",
  time: "Hora",
  venue: "Sede",
  published: "Publicación",
  homeUniform: "Camiseta local",
  awayUniform: "Camiseta visitante",
  homeExclusiveUniform: "Uniforme exclusivo local",
  awayExclusiveUniform: "Uniforme exclusivo visitante",
  homeSocks: "Medias local",
  awaySocks: "Medias visitante",
  // FTR-0039
  homeParticipant: "Participante 1",
  awayParticipant: "Participante 2",
  startTime: "Hora de inicio",
  endTime: "Hora de fin",
  institution: "Institución",
  institutionName: "Institución",
  venueId: "Sede",
  notes: "Notas",
  color: "Color",
  state: "Estado",
  sortOrder: "Orden",
  name: "Nombre",
  abrev: "Abreviatura",
  address: "Dirección",
  siteweb: "Sitio web",
  linkMap: "Mapa",
  iddepartamento: "Departamento",
  idcountry: "País",
  tipo: "Tipo",
  status: "Estado",
  result_state: "Estado del resultado",
  gold: "Oro",
  silver: "Plata",
  bronze_1: "Bronce",
  bronze_2: "Bronce (2.º)",
  athlete: "Deportista",
  category: "Prueba",
  registration: "Inscripción",
  image_path: "Imagen",
  periodo: "Periodo",
  startdate: "Inicio",
  enddate: "Fin",
  place: "Lugar",
  idsport: "Deporte",
  idsports: "Deportes",
  username: "Usuario",
  displayName: "Nombre del delegado",
  email: "Correo",
  phone: "Teléfono",
  capabilities: "Capacidades",
  password: "Contraseña",
  sessions: "Sesiones",
  label: "Etiqueta",
  idtype: "Tipo de uniforme",
  tone: "Tono",
  order: "Orden",
  primaryColor: "Color principal",
  secondaryColor: "Color secundario",
  description: "Descripción",
  closesAt: "Fecha límite",
  reason: "Motivo",
  // Elegibles Rugby (FTR-0015).
  profileKey: "Perfil",
  starterLimit: "Titulares",
  starterCountMode: "Modo de titulares",
  starterEnforcement: "Aplicación de titulares",
  eligibleMax: "Máximo de elegibles",
  eligibleEnforcement: "Aplicación de elegibles",
  confirmed: "Confirmada",
  scopeType: "Alcance",
  minutesBeforeKickoff: "Minutos antes del inicio",
  allowedChanges: "Cambios permitidos",
  exhaustionBehavior: "Al agotar cambios",
  extraChanges: "Cambios extra",
  version: "Versión",
  eligibles: "Elegibles",
  starters: "Titulares",
  count: "Partidos",
  closesAtLima: "Fecha límite (Lima)",
  matchState: "Estado del partido",
};

// Nombres de matches.state (mismos ids del backend; FTR-0015 solo registra el inicio 1 → 2).
const MATCH_STATE_LABELS = { [-2]: "Eliminado", [-1]: "No definido", 0: "No habilitado", 1: "Habilitado", 2: "En vivo", 3: "Finalizado", 4: "Oficializado" };

// Valores enumerados de elegibles Rugby (FTR-0015).
const RUGBY_ENUM_LABELS = {
  AT_MOST: "Como máximo",
  EXACT: "Exacto",
  WARN: "Advertencia",
  BLOCK: "Bloqueo",
  ALLOW_WITH_WARNING: "Permitir con advertencia",
  EVENT_SPORT: "Evento-deporte",
  MATCH: "Partido",
  MATCH_INSTITUTION: "Extensión por institución",
  SEVENS: "Sevens",
  XV: "XV",
  M18: "M18",
  CUSTOM: "Personalizado",
};
const RUGBY_ENUM_FIELDS = new Set([
  "starterCountMode", "starterEnforcement", "eligibleEnforcement", "exhaustionBehavior", "scopeType", "profileKey",
]);

// Estados de cuentas de delegado (FTR-0013); el resto de entidades usa 1/0.
const STATUS_TEXT_LABELS = { ACTIVE: "Activo", DISABLED: "Desactivado" };

export const formatAuditValue = (value, field) => {
  // Restablecer contraseña / revocar sesiones solo registran que ocurrió, nunca un valor.
  if (field === "password" || field === "sessions") return "No se registra";
  if (field === "capabilities" && Array.isArray(value)) {
    return value.length ? value.map((item) => PORTAL_CAPABILITY_LABELS[item] || item).join(", ") : "Ninguna";
  }
  if (value === null || value === undefined || value === "") return "Sin definir";
  if (field === "published") return Number(value) === 1 ? "Oficial" : "Borrador";
  // Opciones de uniforme y plazos (FTR-0014): claves del catálogo, tono/tipo y fecha límite en hora de Lima.
  if (field === "primaryColor" || field === "secondaryColor") return UNIFORM_COLOR_NAMES[value] || String(value);
  if (field === "tone") return UNIFORM_TONE_LABELS[Number(value)] || String(value);
  if (field === "idtype") return UNIFORM_TYPE_LABELS[Number(value)] || String(value);
  if (field === "closesAt") return `${formatLimaDateTime(utcToLimaLocal(value)) || String(value)} (Lima)`;
  if (field === "closesAtLima") return `${formatLimaDateTime(value) || String(value)} (Lima)`;
  if (field === "confirmed") return value ? "Sí" : "No";
  if (RUGBY_ENUM_FIELDS.has(field)) return RUGBY_ENUM_LABELS[value] || String(value);
  if (field === "matchState") return MATCH_STATE_LABELS[Number(value)] || String(value);
  if (field === "order" && Array.isArray(value)) {
    return value.map((item) => `Opción ${item.id}: orden ${item.order}`).join(" · ") || "Sin definir";
  }
  if ((field === "state" || field === "status") && STATUS_TEXT_LABELS[value]) return STATUS_TEXT_LABELS[value];
  if (field === "state" || field === "status") return Number(value) === 1 ? "Activo" : "Inactivo";
  if (typeof value === "object") return value?.name || value?.abbreviation || JSON.stringify(value);
  return String(value);
};

export const getAuditContextTitle = (item) => item?.context?.title || item?.summary || "Cambio registrado";
export const getAuditContextSubtitle = (item) => item?.context?.subtitle || null;

export const getTrainingSnapshot = (item) => {
  if (item?.action === "delete") return item?.before || item?.context?.training || {};
  return item?.after || item?.context?.training || item?.before || {};
};


export const getAuditDisplaySummary = (item) => {
  const contextTitle = item?.context?.title;
  if (!contextTitle) return item?.summary || "Cambio registrado";
  if (["match_schedule", "match_publication"].includes(item?.entityType)) {
    const fields = (item?.changedFields || []).map((field) => FIELD_LABELS[field] || field);
    if (item?.action === "publish") return `Publicó · ${contextTitle}`;
    if (item?.action === "unpublish") return `Envió a borrador · ${contextTitle}`;
    return `${item?.action === "bulk_update" ? "Actualización masiva" : "Actualizó"}${fields.length ? ` ${fields.join(", ")}` : ""} · ${contextTitle}`;
  }
  return item?.summary || contextTitle;
};
